import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Box, Button, Checkbox, LinearProgress } from "@material-ui/core";
import * as _ from 'lodash';
import { Formik, Field } from "formik";

import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";

import AppTable from "../Table/AppTable";
import TablePaginationActions from "../Table/TablePaginationActions";
import AppUtil from "../../../../utilities/AppUtil";
import AppButton from "../../../../components/CustomButtons/AppButton";
import AppColor from "../../../../utilities/AppColor";
import InputSearch from "../../../../components/Input/InputSearch";
import AppConfirmDialog from "../../../../components/Dialog/AppConfirmDialog";
import AppDialog from "../../../../components/Dialog/AppDialog";
import AppInput from "../../../../components/Input/AppInput";
import * as Yup from "yup";
import {
  fetchSpecialtyDetail,
  fetchSpecialtyPaging,
  reduceCreateSpecialty,
  reduceDeleteSpecialty, reduceUpdateSpecialty
} from "../../../../store/slices/specialty";
import AppConfig from "config/AppConfig";

const { PATTERNS } = AppConfig;

const headCells = [
  {
    id: "name",
    numeric: "left",
    disablePadding: false,
    label: "label.specialty_name",
    sort: true,
  },
  {
    id: "refCode",
    numeric: "left",
    disablePadding: false,
    label: "label.ref_code",
    sort: false,
  },
  {
    id: "actions",
    numeric: "center",
    disablePadding: false,
    label: "label.actions",
    sort: false,
  },
];

const INITIALIZE_FORM = {
  id: '',
  name: ''
}

const SpecialtyTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [queries, setQueries] = useState({
    sort: false,
    page: 1,
    size: 10,
    searchKey: "",
    sortField: "createdDate",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formModalTitle, setFormModalTitle] = useState('title.create_specialty')

  const [initializeForm, setInitializeForm] = useState({ ...INITIALIZE_FORM });
  const [submitting, setSubmitting] = useState(false);

  const [searchKey, setSearchKey] = useState("");

  const emptyRows = data.length === 0;

  const _onEdit = async (row) => {
    const { payload } = await dispatch(fetchSpecialtyDetail(row.id));
    if (payload) {
      setInitializeForm({
        ...payload
      });
    } else {
      setInitializeForm({
        ...row
      });
    }
    setFormModalTitle('title.edit_specialty')
    setShowCreateModal(true);
  }

  const _submit = async (data) => {
    setSubmitting(true);
    const request = !data.id ? reduceCreateSpecialty : reduceUpdateSpecialty;
    const requestParams = !data.id ? data : { id: data.id, params: data };
    const { payload } = await dispatch(request(requestParams));
    setSubmitting(false);
    if (payload) {
      _loadData(queries);
      setShowCreateModal(false);
    }
  }

  const _handleDelete = async () => {
    const params = {
      ids: selected
    }
    await dispatch(reduceDeleteSpecialty(params));
    setShowDeleteModal(false);
    if((data.length === 1 || selected.length === data.length) && queries.page > 1) {
      _loadData({ ...queries, page: queries.page -1});
    } else {
    _loadData({ ...queries, searchKey: queries.searchKey, sort: queries.sort });
    }
    setSelected([]);
  }
  const _handleSelect = (id) => {
    if (!selected.includes(id)) {
      setSelected([...selected, id]);
    } else {
      setSelected(selected.filter(item => item !== id))
    }
  }

  const _handleSelectAll = () => {
    if (data.length === selected.length) {
      setSelected([]);
    } else {
      setSelected(_.union(selected, data.map(item => item.id)));
    }
  }

  const handleChangePage = (event, newPage) => {
    _loadData({ ...queries, page: newPage + 1 });
    AppUtil.scrollToTopById("main-panel");
  };

  const handleChangeRowsPerPage = (event) => {
    _loadData({
      ...queries,
      page: 1,
      size: parseInt(event.target.value),
    });
    AppUtil.scrollToTopById("main-panel");
  };

  const onRequestSort = (event, property) => {
    _loadData({
      ...queries,
      sortField: property,
      sort:
        property === queries.sortField ? queries.sort !== true : true,
    });
  };

  const onSearch = (value) => {
    setSearchKey(value);
    _loadData({
      ...queries,
      searchKey: value,
      page: 1,
    });
  };

  const _loadData = async ({
    page, size, searchKey, sort, sortField
  } = {}) => {
    const newQueries = {
      ...queries,
    };
    if (page) {
      newQueries.page = page;
    }
    if (size) {
      newQueries.size = size;
    }
    if (sortField) {
      newQueries.sortField = sortField;
    }

    newQueries.searchKey = searchKey || "";
    newQueries.sort = !(!sort || false);
    setLoading(true);
    const result = await dispatch(fetchSpecialtyPaging(newQueries));
    const { payload } = result;
    if (payload) {
      setData(payload.content);
      setTotalElements(payload.totalElements);
    }
    setQueries(newQueries);
    setLoading(false);
  };

  useEffect(() => {
    _loadData();
  }, []);

  return (
    <div>
      <div>
        <AppButton
          label={t('button.create')}
          onClick={() => {
            setInitializeForm({
              ...INITIALIZE_FORM
            });
            setFormModalTitle('title.create_specialty');
            setShowCreateModal(true);
          }}
          style={{
            marginRight: '1rem'
          }}
        />
        <AppButton
          label={t('button.delete')}
          buttonColor={AppColor.pink}
          onClick={() => setShowDeleteModal(true)}
          disabled={selected.length === 0}
        />
      </div>
      <Box my={2}>
        <InputSearch
          onSearch={onSearch}
          searchText={searchKey}
        />
      </Box>
      <AppTable
        onRequestSort={onRequestSort}
        order={queries.sort === true ? "asc" : "desc"}
        orderBy={queries.sortField}
        rowCount={data.length}
        headCells={headCells}
        numSelected={selected.length}
        onSelectAllClick={_handleSelectAll}
      >
        <TableBody>
          {loading && (<TableRow>
            <TableCell colSpan={4} padding="none">
              <LinearProgress />
            </TableCell>
          </TableRow>)}
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.indexOf(row.id) !== -1}
                  inputProps={{ 'aria-labelledby': row.id }}
                  onClick={() => _handleSelect(row.id)}
                  color="primary"
                />
              </TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.refCode}</TableCell>
              <TableCell className={'text-center'}>
                <Button variant="text" onClick={() => _onEdit(row)}>
                  <i className="fas fa-edit" />
                </Button>
              </TableCell>
            </TableRow>
          ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 53 }}>
              <TableCell colSpan={headCells.length} className="text-center">
                {t("label.no_data")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              colSpan={headCells.length}
              count={totalElements}
              rowsPerPage={queries.size}
              page={queries.page - 1}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage={`${t("label.rows_per_page")}:`}
              labelDisplayedRows={({ from, to, count }) =>
                t("label.from_to", {
                  from,
                  to,
                  count:
                    count !== -1 ? count : `${t("text.more_than")} ${to}`,
                })
              }
              style={{
                overflowX: 'hidden'
              }}
            />
          </TableRow>
        </TableFooter>
      </AppTable>
      <AppConfirmDialog
        open={showDeleteModal}
        onConfirm={() => _handleDelete()}
        onClose={() => setShowDeleteModal(false)}
        title={t('title.delete_specialties')}
        content={t('text.delete_specialties')}
      />
      <AppDialog
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={t(formModalTitle)}
      >
        <Formik
          initialValues={initializeForm}
          onSubmit={_submit}
          enableReinitialize
          validationSchema={Yup.object().shape({
            name: Yup.string()
              .required('validation.name.required')
              .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces')
              .max(64, {
                message: 'validation.max_length',
                value: 64
              })
          })}
        >
          {({ handleSubmit }) => (
            <>
              <Box>
                <Field
                  label={t('label.specialty_name')}
                  component={AppInput}
                  name="name"
                  required
                />
              </Box>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <AppButton
                  label={t('label.back')}
                  mode="outlined"
                  onClick={() => setShowCreateModal(false)}
                  className="mr-2"
                />
                <AppButton
                  label={t('button.save')}
                  onClick={handleSubmit}
                  disabled={submitting}
                  isLoading={submitting}
                />
              </Box>
            </>
          )}
        </Formik>
      </AppDialog>
    </div>
  );
};

export default SpecialtyTable;
